/*
 * -----------------------------------------------------------------------
 *  <copyright file="custom-pager.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';

import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel } from 'react-responsive-carousel';

import { useTranslation, useBoolean } from 'hooks';

import './styles.scss';

interface ICustomPager {
    items: any[];
    itemsForPage: number;
    isCollapseButton: boolean;
    renderChild: any;
    changeItemsForPage?: Function;
    disabled?: boolean;
    isExpandedChart?: boolean;
}

const CustomPager = ({
    items,
    itemsForPage,
    isCollapseButton,
    renderChild,
    changeItemsForPage,
    disabled,
    isExpandedChart,
}: ICustomPager) => {
    const { t } = useTranslation();

    const [data, setData] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [amountPages, setAmountPages] = useState(0);

    const isCollapsed = useBoolean(false);

    const splitArrayIntoArrays = (array: any[], amount: number) => {
        const arrays = [];
        const arrayItems = Object.assign([], array);
        let amountOfPages = 0;
        while (arrayItems.length > 0) {
            if (arrayItems.length < amount) {
                const length = amount - arrayItems.length;
                for (let i = 0; i < length; i++) {
                    arrayItems.push({ empty: true });
                }
            }
            arrays.push(arrayItems.splice(0, amount));
            amountOfPages += 1;
        }
        return {
            data: arrays,
            amountPages: amountOfPages,
        };
    };

    useEffect(() => {
        if (items && itemsForPage) {
            const result = splitArrayIntoArrays(items, itemsForPage);
            setData(result.data);
            setAmountPages(result.amountPages);
            setCurrentSlide(0);
        }
    }, [items, itemsForPage]);

    const updateCurrentSlide = (index: number) => {
        if (currentSlide !== index) setCurrentSlide(index);
    };

    return (
        <div className={`custom-pager-wrapper ${isExpandedChart ? 'expanded-chart' : ''}`}>
            <div className="custom-pager-pagination">
                <FontAwesomeIcon
                    icon={['fal', 'chevron-left']}
                    className={`${currentSlide === 0 ? 'disabled' : ''} pager-icon`}
                    onClick={() => {
                        if (currentSlide > 0) setCurrentSlide(currentSlide - 1);
                    }}
                    data-testid="page-previous"
                />
                <p className="page-info">{t('pageInfo', { current: currentSlide + 1, all: amountPages })}</p>
                <FontAwesomeIcon
                    icon={['fal', 'chevron-right']}
                    className={`${currentSlide === amountPages - 1 || amountPages === 0 ? 'disabled' : ''} pager-icon`}
                    onClick={() => {
                        if (currentSlide !== amountPages - 1) setCurrentSlide(currentSlide + 1);
                    }}
                    data-testid="page-next"
                />
            </div>
            {data?.length ? (
                <Carousel autoPlay={false} selectedItem={currentSlide} showThumbs={false} onChange={updateCurrentSlide}>
                    {data.map((item: any, index1: number) => (
                        <div key={index1} className="pager-carousel-wrapper-item">
                            {item.map((el: any, index2: number) => (
                                <div className="pager-carousel-item panel-item" key={index2}>
                                    {renderChild(el)}
                                </div>
                            ))}
                        </div>
                    ))}
                </Carousel>
            ) : (
                <div className="no-items-wrapper">
                    <p>{t('noResults')}</p>
                </div>
            )}
            {isCollapseButton ? (
                <div
                    className={`collapse-button-pager ${disabled ? 'disabled' : ''}`}
                    onClick={() => {
                        if (!disabled) {
                            isCollapsed.setValue(!isCollapsed.value);
                            setCurrentSlide(0);
                            changeItemsForPage(!isCollapsed.value);
                        }
                    }}
                >
                    <FontAwesomeIcon
                        icon={['fas', isCollapsed.value ? 'caret-down' : 'caret-up']}
                        className="collapse-icon"
                    />
                </div>
            ) : null}
        </div>
    );
};

export default CustomPager;
